/**
 * Status Field Type
 *
 * Provides configuration and renderers for status (Active/Inactive) fields:
 * - Read-only column renderer (chip display)
 * - Editable column renderer (chip with dropdown)
 * - Type registry configuration
 * - Form field renderer
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { ComboBox, Chip } from 'components';
import type { ComboBoxOption } from 'components/inputs/types';
import { CaretDown } from '@phosphor-icons/react';
import { ChipCell, TableTooltip } from '../../components';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
  TypeConfig,
} from '../../types';
import { validateFieldValue } from '../../validation';
import { FieldError, shouldShowFieldError } from './fieldError';
import { S } from './columnWidths';
import { StatusColors } from './columnColors';

// Import shared cell value utility (status uses boolean values)
import { getCellBooleanValue } from './cellValue';

// ============================================================================
// Type Registry Configuration
// ============================================================================

/**
 * Status type configuration for the type registry
 * Displays Active/Inactive for boolean values
 */
export const statusTypeConfig: TypeConfig = {
  options: [
    { id: true, label: 'Active' },
    { id: false, label: 'Inactive' },
  ] as const,
  chipColors: StatusColors,
};

// ============================================================================
// Form Field Renderer
// ============================================================================

// Store reference to getTypeConfig to avoid circular dependencies
let getTypeConfigFn: ((type: string) => TypeConfig | undefined) | null = null;

/**
 * Set the getTypeConfig function (called from index.ts to avoid circular deps)
 */
export const setGetTypeConfig = (fn: (type: string) => TypeConfig | undefined): void => {
  getTypeConfigFn = fn;
};

/**
 * Render a status (Active/Inactive) field for forms
 */
export const renderStatusField = ({
  col,
  value,
  valueForValidation,
  onChange,
  disabled,
  bulkEditContext,
  validationContext,
}: FormFieldProps): React.ReactNode => {
  const typeConfig = getTypeConfigFn ? getTypeConfigFn('status') : statusTypeConfig;
  if (!typeConfig) {
    throw new Error(`Type config not found for type: status`);
  }

  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;
  const valueEmpty = value === null || value === undefined || value === '';
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  // Convert boolean options to ComboBox-compatible format
  const comboBoxOptions: ComboBoxOption[] = typeConfig.options.map(o => ({
    id: String(o.id),
    label: o.label,
  }));

  // Find the matching option object for the current value
  const currentValueStr = value !== undefined && value !== '' ? String(value) : '';
  const selectedOption = comboBoxOptions.find(o => o.id === currentValueStr) || null;

  // Use Zod validation to get error message (valueForValidation for bulk-edit "keep current")
  const valueToValidate = valueForValidation !== undefined ? valueForValidation : value;
  const validation = validateFieldValue({ ...col, type: 'status' }, valueToValidate);
  const errorMessage = !validation.success ? validation.error : undefined;
  const showError = shouldShowFieldError(col.key, validationContext);

  const handleChange = (selectedValue: ComboBoxOption | null) => {
    if (selectedValue && typeof selectedValue === 'object' && 'id' in selectedValue) {
      const statusValue = selectedValue.id === 'true';
      onChange(col.key, statusValue);
    } else {
      onChange(col.key, undefined);
    }
    validationContext?.onFieldTouched?.(col.key);
  };

  return (
    <div key={col.key}>
      <ComboBox
        id={col.key}
        label={col.label}
        placeholder={showEmptyChip ? '' : col.placeholder}
        disabled={disabled}
        inputVariant="outlined"
        labelSeparate
        forcePopupIcon
        popupIcon={<CaretDown size={20} />}
        freeSolo={false}
        options={comboBoxOptions}
        value={selectedOption}
        onChange={(_, selectedValue, reason) => {
          if (reason === 'clear' && bulkEditContext?.isBulkEdit) {
            bulkEditContext.onKeepCurrent();
            return;
          }
          handleChange(selectedValue as ComboBoxOption | null);
        }}
        InputProps={showEmptyChip ? { startAdornment: emptyDisplayNode } : undefined}
      />
      <FieldError error={errorMessage} show={showError} />
    </div>
  );
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

// Store reference to resolveFieldConfig to avoid circular dependencies
let resolveFieldConfigFn: ((col: { id: string; type?: string }) => unknown) | null = null;

/**
 * Set the resolveFieldConfig function (called from index.ts to avoid circular deps)
 */
export const setResolveFieldConfig = (
  fn: (col: { id: string; type?: string }) => unknown
): void => {
  resolveFieldConfigFn = fn;
};

/**
 * Helper to get status config
 */
const getStatusConfig = (
  colId: string
): { options: TypeConfig['options']; chipColors: TypeConfig['chipColors'] } => {
  const resolved = resolveFieldConfigFn
    ? (resolveFieldConfigFn({ id: colId, type: 'status' }) as {
        kind: string;
        options: TypeConfig['options'];
        chipColors: TypeConfig['chipColors'];
      } | null)
    : {
        kind: 'status',
        options: statusTypeConfig.options,
        chipColors: statusTypeConfig.chipColors,
      };

  if (!resolved || resolved.kind !== 'status') {
    throw new Error(`Failed to resolve status config for column: ${colId}`);
  }

  return resolved;
};

/**
 * Status read-only renderer - displays Active/Inactive chip
 * Uses col.id as the data field name
 */
export const statusReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const { options, chipColors } = getStatusConfig(col.id);

  return {
    cell: (context: CellContext<T, unknown>) => {
      // Use shared getCellBooleanValue to respect accessorFn (important for custom fields)
      const value = getCellBooleanValue(context, col.id);
      const option = options.find(o => o.id === value) || options[1];
      const background = value ? chipColors.true : chipColors.false;

      return (
        <TableTooltip title={option.label}>
          <Chip
            sx={{ padding: '4px 16px' }}
            isBordered={false}
            isBackgroundFilled
            isTooltip={false}
            label={option.label}
            color={background}
            background={background}
          />
        </TableTooltip>
      );
    },
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Status editable renderer - displays chip with dropdown for editing
 * Uses col.id as the data field name
 */
export const statusEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const { options: statusOptions, chipColors } = getStatusConfig(col.id);
  const comboBoxOptions = statusOptions.map(o => ({
    id: String(o.id),
    label: o.label,
  }));

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellBooleanValue(context, col.id);
      const option = statusOptions.find(o => o.id === value) || statusOptions[1];
      const background = value ? chipColors.true : chipColors.false;

      return (
        <ChipCell
          context={context}
          label={option.label}
          background={background}
          color={background}
          editable
          options={comboBoxOptions}
          onUpdate={selected => {
            if (col.onUpdate) {
              const boolValue = selected.id === 'true';
              col.onUpdate(context.row.original, { [col.id]: boolValue } as Partial<T>);
            }
          }}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Status field type configuration
 */
export const statusFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Select ${label.toLowerCase()}...`,
  ...S,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const statusReadOnly: ColumnRenderer = statusReadOnlyRenderer;

/** Editable renderer with proper typing */
export const statusEditable: ColumnRenderer = statusEditableRenderer;
